import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { ChevronDown, Megaphone, Ticket, Globe, Wallet, MousePointerClick } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Friendly explainer: how each promoter link works and tracks.
export const PromoterLinkGuide = ({ token }) => {
  const [open, setOpen] = useState(true);
  const [handle, setHandle] = useState('');
  const origin = 'eventism.com';

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/promoter/profile/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setHandle(r.data?.profile?.handle || ''))
      .catch(() => {});
  }, [token]);

  const tiles = [
    {
      icon: Megaphone,
      color: 'text-fuchsia-400',
      title: 'Your profile page — the link for your bio',
      url: `${origin}/promo/${handle || 'your-handle'}`,
      points: [
        'One evergreen link that never changes — perfect for Instagram/TikTok bios',
        'Every campaign you accept appears on it automatically',
        'Fans can follow you — they get an email each time you take on a new event',
      ],
    },
    {
      icon: Ticket,
      color: 'text-emerald-400',
      title: 'Your event link — tracks every click & sale',
      url: `${origin}/p/YOUR-CODE/event/…`,
      points: [
        'Each campaign gives you a unique link with your tracking code built in',
        'Every click is counted, and buyers are credited to you for 30 days — even if they come back next week to buy',
        'Your commission lands automatically on every tracked sale',
      ],
    },
    {
      icon: Globe,
      color: 'text-indigo-400',
      title: 'Partner-site links — sell on other websites',
      url: 'partnersite.com/?p=YOUR-CODE',
      points: [
        'Some events have their ticket widget on partner websites (the organizer lists them, or build your own link)',
        'Add ?p=YOUR-CODE to that site and sales made there still credit you — same 30-day memory',
        '"Where your sales come from" shows which placement converts best',
      ],
    },
  ];

  return (
    <Card className="border-indigo-500/30 bg-gradient-to-br from-indigo-500/5 to-fuchsia-500/5" data-testid="promoter-link-guide">
      <CardContent className="p-4">
        <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-2" data-testid="link-guide-toggle">
          <h2 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
            <MousePointerClick className="h-4 w-4 text-indigo-400" /> How your links work — your 60-second guide
          </h2>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="mt-4 space-y-3">
            <div className="grid md:grid-cols-3 gap-3">
              {tiles.map((t, i) => (
                <div key={i} className="rounded-xl border border-border/70 bg-background/60 p-3.5" data-testid={`link-guide-tile-${i}`}>
                  <t.icon className={`h-5 w-5 mb-2 ${t.color}`} />
                  <p className="font-semibold text-sm leading-snug">{t.title}</p>
                  <p className="text-[11px] font-mono text-muted-foreground mt-1 truncate">{t.url}</p>
                  <ul className="mt-2 space-y-1.5">
                    {t.points.map((pt, j) => (
                      <li key={j} className="text-[11px] text-muted-foreground leading-relaxed flex gap-1.5">
                        <span className={`${t.color} flex-shrink-0`}>•</span> {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="flex items-start gap-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20 px-3 py-2.5">
              <Wallet className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                <span className="font-semibold text-foreground">Getting paid:</span> commissions from every tracked sale land in your wallet above, release 3 days after the event ends, and cash out to your bank or debit card via Stripe. No invoices, no chasing anyone.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PromoterLinkGuide;
